/**
 * Outil « Roue de loterie » (Boîte à outils, 1re → Tle).
 *
 * On règle la roue (secteurs en douzièmes, gain de chaque secteur) et la
 * mise. X, la variable aléatoire égale au gain algébrique (gain − mise), a sa
 * loi écrite en fractions, son espérance E(X), sa variance V(X) et son écart
 * type σ(X). On joue 1, 10, 100 ou 1 000 parties : le gain moyen, tracé
 * partie après partie, se rapproche de E(X). « Rendre équitable » : trouver
 * la mise qui annule l'espérance (défis où elle tombe sur un nombre entier
 * d'euros). Logique : `lottery.ts`.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@jeraisonne/ui';
import {
  Note,
  PRIMARY_BUTTON,
  SECONDARY_BUTTON,
  Segmented,
  Stepper,
  ToggleChip,
} from '../../ui/controls';
import { value } from '../../ui/exact';
import { formatNumber, frenchSpaces } from '../../ui/format';
import { logSampleIndices, seededRng } from '../../ui/random';
import { FIGURE_BG_CLASS, HaloText } from '../../ui/svg-parts';
import {
  FAIR_CHALLENGES,
  PARTS,
  PRESETS,
  complete,
  euros,
  expectation,
  expectationText,
  fairStake,
  fracText,
  law,
  play,
  probText,
  sd,
  sdExact,
  spin,
  targetAngle,
  totalParts,
  varianceText,
  verdict,
  type Game,
  type Preset,
} from './lottery';

const COLORS = [
  'var(--color-primary-500)',
  'var(--color-accent-500)',
  'var(--color-success-500)',
  'var(--color-error-500)',
  'var(--color-primary-300)',
  'var(--color-ink-400)',
];
const MAX_SECTORS = 6;
const MAX_STAKE = 50;
const SPIN_MS = 1100;
const NB = String.fromCharCode(0xa0);
const OFF = 'disabled:opacity-40';

/** « 1 000 » : espace fine insécable entre les milliers */
const group = (n: number) =>
  String(n).replace(/\B(?=(\d{3})+(?!\d))/g, String.fromCharCode(0x202f));
const num = (v: number, d = 2) => formatNumber(v, d).replace('-', '−');

/** animations coupées si le système le demande */
const reduceMotion = () =>
  typeof window !== 'undefined' &&
  !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

function Wheel({ g, angle, last }: { g: Game; angle: number; last: number | null }) {
  const R = 110;
  const C = 130;
  let acc = 0;
  const slices = g.sectors.map((s, i) => {
    const a0 = (acc / PARTS) * 2 * Math.PI;
    acc += s.k;
    const a1 = (acc / PARTS) * 2 * Math.PI;
    return { i, s, a0, a1 };
  });
  const pt = (a: number, r: number) => [C + r * Math.sin(a), C - r * Math.cos(a)] as const;
  const transition = reduceMotion() ? 'none' : `transform ${SPIN_MS}ms cubic-bezier(.2,.8,.2,1)`;
  return (
    <svg
      viewBox="0 0 260 262"
      className={cn('mx-auto block w-full max-w-[18rem] select-none rounded-2xl', FIGURE_BG_CLASS)}
      role="img"
      aria-label={`Roue : ${g.sectors.map((s) => `${s.k}/12 pour ${euros(s.gain)}`).join(', ')}.`}
      data-testid="lw-wheel"
    >
      <g
        style={{
          transform: `rotate(${angle}deg)`,
          transformOrigin: `${C}px ${C}px`,
          transition,
        }}
      >
        {slices.map(({ i, s, a0, a1 }) => {
          if (s.k <= 0) return null;
          const [x0, y0] = pt(a0, R);
          const [x1, y1] = pt(a1, R);
          const large = a1 - a0 > Math.PI ? 1 : 0;
          const d =
            s.k === PARTS
              ? `M${C - R},${C} a${R},${R} 0 1,0 ${2 * R},0 a${R},${R} 0 1,0 ${-2 * R},0`
              : `M${C},${C} L${x0},${y0} A${R},${R} 0 ${large} 1 ${x1},${y1} Z`;
          const [lx, ly] = pt((a0 + a1) / 2, R * 0.64);
          return (
            <g key={i}>
              <path
                d={d}
                fill={COLORS[i % COLORS.length]}
                opacity={last === null || last === i ? 0.9 : 0.55}
                stroke="var(--fig-bg, #fff)"
                strokeWidth={2}
              />
              {/* l'étiquette reste droite pendant que la roue tourne */}
              <g
                style={{
                  transform: `rotate(${-angle}deg)`,
                  transformOrigin: `${lx}px ${ly}px`,
                  transition,
                }}
              >
                <HaloText
                  x={lx}
                  y={ly + 5}
                  textAnchor="middle"
                  fontSize={14}
                  fontWeight={700}
                  fill="var(--color-ink-900)"
                >
                  {euros(s.gain)}
                </HaloText>
              </g>
            </g>
          );
        })}
      </g>
      {/* l'aiguille, en haut */}
      <polygon points={`${C - 9},4 ${C + 9},4 ${C},26`} fill="var(--color-ink-900)" />
      <circle cx={C} cy={C} r={7} fill="var(--color-ink-900)" />
    </svg>
  );
}

/** gain moyen après chaque partie, contre E(X) */
function MeanChart({ xs, e, eText }: { xs: number[]; e: number; eText: string }) {
  const W = 320;
  const H = 160;
  const L = 34;
  const n = xs.length;
  const idx = logSampleIndices(n, 120);
  let s = 0;
  const means: number[] = [];
  for (let i = 0; i < n; i++) {
    s += xs[i]!;
    means.push(s / (i + 1));
  }
  const pts = idx.map((i) => ({ i, m: means[i - 1]! }));
  const lo = Math.floor(Math.min(e - 1, ...pts.map((p) => p.m)));
  const hi = Math.ceil(Math.max(e + 1, ...pts.map((p) => p.m)));
  const tx = (i: number) => L + (Math.log(i) / Math.log(Math.max(n, 2))) * (W - L - 10);
  const ty = (v: number) => 10 + ((hi - v) / (hi - lo)) * (H - 32);
  const d = pts
    .map((p, k) => `${k ? 'L' : 'M'}${tx(p.i).toFixed(1)},${ty(p.m).toFixed(1)}`)
    .join(' ');
  const step = Math.max(1, Math.ceil((hi - lo) / 6));
  const yTicks: number[] = [];
  for (let v = lo; v <= hi; v += step) yTicks.push(v);
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className={cn(
        'border-ink-100 mx-auto block w-full max-w-[26rem] rounded-2xl border',
        FIGURE_BG_CLASS,
      )}
      role="img"
      aria-label={`Gain moyen après ${group(n)} parties : environ ${num(means.at(-1)!)} euro ; E(X) = ${eText}.`}
      data-testid="lw-chart"
    >
      {yTicks.map((v) => (
        <g key={v}>
          <line
            x1={L}
            x2={W - 6}
            y1={ty(v)}
            y2={ty(v)}
            stroke={v === 0 ? 'var(--color-ink-400)' : 'var(--color-ink-100)'}
          />
          <text x={L - 5} y={ty(v) + 4} textAnchor="end" fontSize={12} fill="var(--color-ink-500)">
            {num(v, 0)}
          </text>
        </g>
      ))}
      <line
        x1={L}
        x2={W - 6}
        y1={ty(e)}
        y2={ty(e)}
        stroke="var(--color-accent-700)"
        strokeWidth={1.5}
        strokeDasharray="6 4"
      />
      <path d={d} fill="none" stroke="var(--color-primary-500)" strokeWidth={2} />
      <HaloText
        x={W - 8}
        y={ty(e) - 6}
        textAnchor="end"
        fontSize={12}
        fontWeight={700}
        fill="var(--color-accent-700)"
      >
        {`E(X) = ${eText}`}
      </HaloText>
      {[1, 10, 100, 1000, 10000, 100000]
        .filter((v) => v <= n)
        .map((v) => (
          <text
            key={v}
            x={Math.min(tx(v), W - 24)}
            y={H - 6}
            textAnchor="middle"
            fontSize={12}
            fill="var(--color-ink-500)"
          >
            {group(v)}
          </text>
        ))}
    </svg>
  );
}

function verdictText(g: Game): string {
  const v = verdict(g);
  return v === 'fair'
    ? 'E(X) = 0 : le jeu est équitable.'
    : v === 'player'
      ? 'E(X) > 0 : en moyenne le joueur gagne, le jeu lui est favorable.'
      : 'E(X) < 0 : en moyenne le joueur perd, le jeu est favorable à l’organisateur.';
}

function Editor({ g, onChange }: { g: Game; onChange: (g: Game) => void }) {
  const left = PARTS - totalParts(g);
  const set = (i: number, patch: Partial<Game['sectors'][number]>) =>
    onChange({ ...g, sectors: g.sectors.map((s, j) => (j === i ? { ...s, ...patch } : s)) });
  const biggest = g.sectors.reduce((b, s, i) => (s.k > g.sectors[b]!.k ? i : b), 0);
  const canAdd = g.sectors.length < MAX_SECTORS && (left > 0 || (g.sectors[biggest]?.k ?? 0) >= 2);
  const add = () => {
    if (left > 0) {
      onChange({ ...g, sectors: [...g.sectors, { k: left, gain: 0 }] });
      return;
    }
    // roue complète : le nouveau secteur prend un douzième au plus gros
    onChange({
      ...g,
      sectors: [
        ...g.sectors.map((s, i) => (i === biggest ? { ...s, k: s.k - 1 } : s)),
        { k: 1, gain: 0 },
      ],
    });
  };
  return (
    <div className="space-y-2">
      {g.sectors.map((s, i) => (
        <div key={i} className="border-ink-100 space-y-1.5 rounded-2xl border p-2">
          <p className="text-ink-700 flex items-center gap-2 text-xs font-semibold">
            <span
              aria-hidden
              className="h-3 w-3 rounded-full"
              style={{ background: COLORS[i % COLORS.length] }}
            />
            {`Secteur ${i + 1}`}
          </p>
          <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
            <Stepper
              label={`Secteur ${i + 1} : part`}
              value={s.k}
              min={1}
              max={s.k + Math.max(0, left)}
              onChange={(k) => set(i, { k })}
              format={(k) => `${k}/12`}
            />
            <Stepper
              label={`Secteur ${i + 1} : gain`}
              value={s.gain}
              min={0}
              max={50}
              onChange={(gain) => set(i, { gain })}
              format={(v) => euros(v)}
            />
          </div>
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          className={cn(SECONDARY_BUTTON, OFF)}
          disabled={!canAdd}
          onClick={add}
        >
          Ajouter un secteur
        </button>
        <button
          type="button"
          className={cn(SECONDARY_BUTTON, OFF)}
          disabled={g.sectors.length <= 1}
          onClick={() => onChange({ ...g, sectors: g.sectors.slice(0, -1) })}
        >
          Retirer le dernier
        </button>
      </div>
    </div>
  );
}

function LawTable({ g }: { g: Game }) {
  const rows = law(g);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-center text-sm" data-testid="lw-law">
        <caption className="text-ink-600 pb-1 text-left text-xs">
          {frenchSpaces(
            'Soit X la variable aléatoire égale au gain algébrique (gain − mise). Loi de X :',
          )}
        </caption>
        <tbody>
          <tr className="border-ink-100 border-b">
            <th scope="row" className="text-ink-600 py-1 pr-2 text-left font-semibold">
              xᵢ
            </th>
            {rows.map((r) => (
              <td key={r.x} className="text-ink-900 whitespace-nowrap py-1 font-semibold">
                {euros(r.x)}
              </td>
            ))}
          </tr>
          <tr>
            <th
              scope="row"
              className="text-ink-600 whitespace-nowrap py-1 pr-2 text-left font-semibold"
            >
              P(X = xᵢ)
            </th>
            {rows.map((r) => (
              <td key={r.x} className="text-ink-900 py-1">
                {probText(r.p)}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export function LotteryWheelTool({ seed }: { /** graine des tirages (tests) */ seed?: number }) {
  const rng = useMemo(() => (seed === undefined ? Math.random : seededRng(seed)), [seed]);
  const [mode, setMode] = useState<'play' | 'fair'>('play');
  const [pid, setPid] = useState(PRESETS[0]!.id);
  const [g, setG] = useState<Game>(PRESETS[0]!.game);
  const [edit, setEdit] = useState(false);
  const [xs, setXs] = useState<number[]>([]);
  const [angle, setAngle] = useState(0);
  const [last, setLast] = useState<number | null>(null);
  const [spinning, setSpinning] = useState(false);
  const [hint, setHint] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const presets = mode === 'play' ? PRESETS : FAIR_CHALLENGES;
  const ok = complete(g);
  const e = expectation(g);
  const eText = fracText(e);
  const eFull = `${eText}${eText.includes('/') ? ` ≈ ${num(value(e))}` : ''}${NB}€`;

  const stopSpin = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    setSpinning(false);
  };
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const change = (next: Game) => {
    stopSpin();
    setG(next);
    setXs([]);
    setLast(null);
  };
  const choose = (p: Preset) => {
    setPid(p.id);
    setHint(false);
    change(p.game);
  };
  const one = () => {
    if (spinning) return;
    const i = spin(g, rng);
    setAngle((a) => targetAngle(g, i, a));
    setLast(null);
    const land = () => {
      timer.current = null;
      setSpinning(false);
      setLast(i);
      setXs((h) => [...h, g.sectors[i]!.gain - g.stake]);
    };
    // le résultat ne s'affiche qu'à l'arrêt de la roue
    if (reduceMotion()) land();
    else {
      setSpinning(true);
      timer.current = setTimeout(land, SPIN_MS);
    }
  };
  const many = (n: number) => {
    if (spinning) return;
    setLast(null);
    setXs((h) => [...h, ...play(g, n, rng)]);
  };
  const mean = xs.length ? xs.reduce((s, x) => s + x, 0) / xs.length : null;
  const fs = fairStake(g);
  const sdx = sdExact(g);

  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Ce qu’on fait"
        value={mode}
        options={[
          { value: 'play', label: 'Jouer' },
          { value: 'fair', label: 'Rendre équitable' },
        ]}
        onChange={(m) => {
          setMode(m);
          choose((m === 'play' ? PRESETS : FAIR_CHALLENGES)[0]!);
        }}
      />
      <div role="group" aria-label="Roue" className="flex flex-wrap gap-1.5">
        {presets.map((p) => (
          <ToggleChip key={p.id} on={p.id === pid} onClick={() => choose(p)}>
            {p.label}
          </ToggleChip>
        ))}
        <button
          type="button"
          aria-expanded={edit}
          onClick={() => setEdit(!edit)}
          className={cn(
            'min-h-11 min-w-11 rounded-xl border px-3 text-sm font-semibold',
            edit
              ? 'border-primary-500 bg-primary-50 text-primary-700'
              : 'border-ink-200 text-ink-600 hover:bg-ink-50',
          )}
        >
          Régler la roue
        </button>
      </div>
      <Wheel g={g} angle={angle} last={last} />
      <div className="w-44">
        <Stepper
          label="Mise"
          value={g.stake}
          min={0}
          max={MAX_STAKE}
          onChange={(stake) => change({ ...g, stake })}
          format={(v) => euros(v)}
        />
      </div>
      {edit && <Editor g={g} onChange={change} />}
      {!ok && (
        <Note live tone="error">
          {frenchSpaces(
            `Les parts doivent faire un tour complet : il reste ${PARTS - totalParts(g)}/12 à attribuer${edit ? '' : ' (Régler la roue)'}.`,
          )}
        </Note>
      )}

      {ok && <LawTable g={g} />}
      {ok && (
        <div className="bg-ink-50 space-y-1 rounded-2xl px-3 py-2 text-sm" data-testid="lw-stats">
          <p className="text-ink-900 font-semibold" data-testid="lw-expectation">
            {frenchSpaces(`${expectationText(g)}${NB}€`)}
          </p>
          <p className="text-ink-800">{frenchSpaces(verdictText(g))}</p>
          <p className="text-ink-800" data-testid="lw-variance">
            {frenchSpaces(varianceText(g))}
          </p>
          <p className="text-ink-800">
            {frenchSpaces(
              `σ(X) = √V(X) ${sdx ? `= ${sdx}` : `≈ ${num(sd(g))}`}${NB}€ (écart type : la dispersion des gains autour de E(X)).`,
            )}
          </p>
        </div>
      )}

      {mode === 'play' && ok && (
        <section aria-label="Jouer" className="space-y-2">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className={cn(PRIMARY_BUTTON, spinning && 'opacity-60')}
              aria-disabled={spinning}
              onClick={one}
            >
              Tourner la roue
            </button>
            {[10, 100, 1000].map((n) => (
              <button
                key={n}
                type="button"
                className={cn(SECONDARY_BUTTON, spinning && 'opacity-40')}
                aria-disabled={spinning}
                onClick={() => many(n)}
              >
                {`${group(n)} parties`}
              </button>
            ))}
            <button
              type="button"
              className={cn(SECONDARY_BUTTON, OFF)}
              onClick={() => change(g)}
              disabled={!xs.length}
            >
              Effacer
            </button>
          </div>
          <p className="text-ink-800 text-sm" aria-live="polite" data-testid="lw-mean">
            {spinning
              ? 'La roue tourne…'
              : mean === null
                ? 'Pas encore de partie.'
                : frenchSpaces(
                    `${group(xs.length)} partie${xs.length > 1 ? 's' : ''}${last !== null ? ` (dernière : ${euros(g.sectors[last]!.gain - g.stake)})` : ''} : gain moyen ≈ ${num(mean)}${NB}€, pour E(X) = ${eFull}.`,
                  )}
          </p>
          {xs.length >= 2 && (
            <figure className="space-y-1">
              <figcaption className="text-ink-500 text-xs font-semibold">
                {frenchSpaces('Gain moyen après n parties (n en échelle logarithmique)')}
              </figcaption>
              <MeanChart xs={xs} e={value(e)} eText={eText} />
            </figure>
          )}
          {xs.length >= 100 && (
            <p className="text-ink-600 text-xs">
              {frenchSpaces(
                'Plus on joue, plus le gain moyen se rapproche de l’espérance : c’est la loi des grands nombres (on l’énoncera en Terminale).',
              )}
            </p>
          )}
        </section>
      )}

      {mode === 'fair' && ok && (
        <section aria-label="Rendre le jeu équitable" className="space-y-2">
          <Note live tone={verdict(g) === 'fair' ? 'success' : 'neutral'}>
            <span data-testid="lw-fair">
              {frenchSpaces(
                verdict(g) === 'fair'
                  ? `Bravo : avec une mise de ${euros(g.stake)}, E(X) = 0, le jeu est équitable.`
                  : `Mise ${euros(g.stake)} : E(X) = ${eFull}. ${verdict(g) === 'player' ? 'Augmente' : 'Baisse'} la mise pour que E(X) = 0.`,
              )}
            </span>
          </Note>
          {verdict(g) !== 'fair' && fs.q !== 1 && (
            <Note live={false} tone="error">
              {frenchSpaces(
                `Avec ces gains, la mise équitable vaudrait ${fracText(fs)}${NB}€ ≈ ${num(value(fs))}${NB}€ : pas un nombre entier d’euros. Change un gain ou une part, ou choisis un défi.`,
              )}
            </Note>
          )}
          {verdict(g) !== 'fair' &&
            fs.q === 1 &&
            (hint ? (
              <Note live>
                {frenchSpaces(
                  'Indice : E(X) = (espérance du gain, mise non déduite) − mise. Calcule d’abord l’espérance du gain : la somme des gains × probabilités.',
                )}
              </Note>
            ) : (
              <button type="button" className={SECONDARY_BUTTON} onClick={() => setHint(true)}>
                Un indice
              </button>
            ))}
        </section>
      )}
    </div>
  );
}
